import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { locationRecordingOperationalNotificationRetryPath } from "@/routes";

type Notification = {
  id: string;
  recording_id: string;
  kind: string;
  state: string;
  recipient: string | null;
  subject: string;
  failure_code: string | null;
  sent_at: string | null;
  history: { state: string; failure_code: string | null; at: string }[];
};
export default function History({
  notifications,
}: {
  recording: { id: string };
  notifications: Notification[];
}) {
  const { auth, i18n, job_context } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  return (
    <JobWorkspace breadcrumbs={[]}>
      <Head title={t("notifications.history")} />
      <PageCanvas padded={!job_context}>
        <NotificationHeading>{t("notifications.history")}</NotificationHeading>
        <p className="text-muted-foreground text-sm">
          {t("notifications.sent_help")}
        </p>
        {notifications.length === 0 && <p>{t("notifications.empty")}</p>}
        {notifications.map((item) => (
          <section key={item.id} className="space-y-3 rounded-lg border p-4">
            <h2 className="font-medium">{item.subject}</h2>
            <p className="break-words">
              {t("notifications.recipient")}:{" "}
              {item.recipient || t("notifications.no_email")}
            </p>
            <p role="status">{t(`notifications.states.${item.state}`)}</p>
            {item.failure_code && (
              <p role="alert">{t(`notifications.${item.failure_code}`)}</p>
            )}
            {(item.state === "failed" || item.state === "sending") && (
              <Form
                action={locationRecordingOperationalNotificationRetryPath(
                  auth.location!.id,
                  item.recording_id,
                  item.id,
                )}
                method="post"
              >
                {({ processing }) => (
                  <Button
                    variant="outline"
                    disabled={
                      processing ||
                      item.failure_code === "reconciliation_required" ||
                      item.failure_code === "superseded" ||
                      item.failure_code === "undeliverable"
                    }
                  >
                    {t("notifications.retry")}
                  </Button>
                )}
              </Form>
            )}
            <details>
              <summary className="cursor-pointer">
                {t("notifications.attempts")}
              </summary>
              <ol className="mt-3 space-y-2">
                {item.history.map((entry, index) => (
                  <li key={index}>
                    {t(`notifications.states.${entry.state}`)} ·{" "}
                    {new Intl.DateTimeFormat(i18n.locale, {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: i18n.time_zone,
                    }).format(new Date(entry.at))}
                  </li>
                ))}
              </ol>
            </details>
          </section>
        ))}
      </PageCanvas>
    </JobWorkspace>
  );
}

function NotificationHeading({ children }: { children: React.ReactNode }) {
  const Tag = usePage().props.job_context ? "h2" : "h1";
  return <Tag className="text-page font-display">{children}</Tag>;
}
